class ScrollLock extends HTMLElement {
  static lockCount = 0;
  static scrollY = 0;
  static isLocked = false;
  static scrollbarWidth = 0;

  connectedCallback() {
    if (this.hasAttribute("active")) {
      this.lock();
    }
    
    // Note: We rely on attributeChangedCallback for attribute changes
    // MutationObserver was causing duplicate calls with attributeChangedCallback
  }
  
  disconnectedCallback() {
    // Clean up any remaining locks when element is removed
    if (ScrollLock.lockCount > 0) {
      this.forceUnlock();
    }
  }

  static get observedAttributes() {
    return ["active"];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "active") {
      // Use setTimeout to ensure the attribute change has been processed
      setTimeout(() => {
        if (this.hasAttribute("active")) {
          this.lock();
        } else {
          this.unlock();
        }
      }, 0);
    }
  }

  getScrollbarWidth() {
    // Return cached value if already calculated
    if (ScrollLock.scrollbarWidth !== undefined && ScrollLock.scrollbarWidth !== 0) {
      return ScrollLock.scrollbarWidth;
    }

    // Check if body exists (in case this is called very early)
    if (!document.body) {
      return 0;
    }

    // Create a temporary div to measure scrollbar width
    const outer = document.createElement('div');
    outer.style.cssText = 'visibility: hidden; position: absolute; top: -9999px; width: 100px; height: 100px; overflow: scroll; msOverflowStyle: scrollbar;';
    document.body.appendChild(outer);

    // Add inner div with content larger than outer to force scrollbar
    const inner = document.createElement('div');
    inner.style.cssText = 'width: 100%; height: 200px;';
    outer.appendChild(inner);

    // The scrollbar width is the difference between offsetWidth and clientWidth
    const scrollbarWidth = outer.offsetWidth - outer.clientWidth;

    outer.parentNode.removeChild(outer);

    return scrollbarWidth;
  }

  lock() {
    // If already locked, don't increment the count
    if (ScrollLock.isLocked) {
      return;
    }
     
    if (ScrollLock.lockCount === 0) {
      const ua = navigator.userAgent || navigator.vendor || window.opera;
      const isiOS = /iP(ad|hone|od)/.test(ua);

      if (isiOS) {
        ScrollLock.scrollY = window.scrollY;
        document.body.style.position = "fixed";
        document.body.style.top = `-${ScrollLock.scrollY}px`;
        document.body.style.left = "0";
        document.body.style.right = "0";
        document.body.style.width = "100%";
        
        // Prevent pull-to-refresh and other touch gestures
        document.body.style.touchAction = "none";
        document.body.style.overscrollBehavior = "none";
        
        // Add touch event listeners to prevent scrolling
        this.addTouchListeners();
      } else {
        // Calculate scrollbar width before hiding it
        ScrollLock.scrollbarWidth = this.getScrollbarWidth();
        
        // Apply padding to body to prevent layout shift
        if (ScrollLock.scrollbarWidth > 0) {
          // Handle RTL (right-to-left) layouts
          const isRTL = document.documentElement.dir === 'rtl' || 
                        getComputedStyle(document.documentElement).direction === 'rtl';
          
          if (isRTL) {
            document.body.style.paddingLeft = `${ScrollLock.scrollbarWidth}px`;
          } else {
            document.body.style.paddingRight = `${ScrollLock.scrollbarWidth}px`;
          }
          
          // Store scrollbar width as CSS variable for use in stylesheets
          document.documentElement.style.setProperty('--scrollbar-width', `${ScrollLock.scrollbarWidth}px`);
        }
        
        // Add class to body for custom styling
        document.body.classList.add('scroll-locked');
        document.body.style.overflow = "hidden";
      }
    }
    ScrollLock.lockCount++;
    ScrollLock.isLocked = true;
  }

  unlock() {
    // Always decrement the lock count when unlocking
    ScrollLock.lockCount = Math.max(ScrollLock.lockCount - 1, 0);
    if (ScrollLock.lockCount === 0) {
      ScrollLock.isLocked = false;
      const isiOS = /iP(ad|hone|od)/.test(navigator.userAgent || navigator.vendor || window.opera);

      if (isiOS) {
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.left = "";
        document.body.style.right = "";
        document.body.style.width = "";
        document.body.style.touchAction = "";
        document.body.style.overscrollBehavior = "";
        window.scrollTo(0, ScrollLock.scrollY || 0);
        
        // Remove touch event listeners
        this.removeTouchListeners();
      } else {
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
        document.body.style.paddingLeft = ""; // Clear both for RTL support
        document.documentElement.style.removeProperty('--scrollbar-width');
        document.body.classList.remove('scroll-locked');
        ScrollLock.scrollbarWidth = 0;
      }
    }
  }

  addTouchListeners() {
    // Store the bound functions so we can remove them later
    this.touchStartHandler = this.handleTouchStart.bind(this);
    this.touchMoveHandler = this.handleTouchMove.bind(this);
    this.touchEndHandler = this.handleTouchEnd.bind(this);
    
    document.addEventListener('touchstart', this.touchStartHandler, { passive: true });
    document.addEventListener('touchmove', this.touchMoveHandler, { passive: false });
    document.addEventListener('touchend', this.touchEndHandler, { passive: true });
  }

  removeTouchListeners() {
    if (this.touchStartHandler) {
      document.removeEventListener('touchstart', this.touchStartHandler);
      document.removeEventListener('touchmove', this.touchMoveHandler);
      document.removeEventListener('touchend', this.touchEndHandler);
      this.touchStartHandler = null;
      this.touchMoveHandler = null;
      this.touchEndHandler = null;
    }
  }

  handleTouchStart(e) {
    // Store the initial touch position and element
    this.startY = e.touches[0].clientY;
    this.startX = e.touches[0].clientX;
    this.startElement = e.target;
    this.isScrolling = false;
  }
  
  handleTouchEnd(e) {
    // Reset touch tracking
    this.startY = null;
    this.startX = null;
    this.startElement = null;
    this.isScrolling = false;
  }

  handleTouchMove(e) {
    // Allow scrolling on elements with data-scroll-lock-scrollable attribute
    const target = e.target;
    const scrollableElement = target.closest('[data-scroll-lock-scrollable]');
    
    // If the touch is within a scrollable element, allow it completely
    if (scrollableElement) {
      this.isScrolling = true;
      return;
    }
    
    // Check if the touch started within a scrollable element
    if (this.startElement) {
      const startScrollableElement = this.startElement.closest('[data-scroll-lock-scrollable]');
      if (startScrollableElement) {
        this.isScrolling = true;
        return;
      }
    }
    
    // Only prevent default for vertical scrolling on the body
    // This prevents pull-to-refresh and body scrolling while allowing horizontal gestures
    const touch = e.touches[0];
    const deltaY = Math.abs(touch.clientY - this.startY);
    const deltaX = Math.abs(touch.clientX - this.startX);
    
    // Only prevent if it's primarily a vertical scroll gesture and started outside scrollable areas
    if (deltaY > deltaX && deltaY > 10 && !this.isScrolling) {
      e.preventDefault();
    }
  }
  
  /**
   * Check the current state of the scroll lock
   */
  getState() {
    return {
      hasActiveAttribute: this.hasAttribute('active'),
      lockCount: ScrollLock.lockCount,
      scrollY: ScrollLock.scrollY,
      bodyOverflow: document.body.style.overflow,
      bodyPosition: document.body.style.position,
      isLocked: ScrollLock.isLocked,
      isLockedByCount: ScrollLock.lockCount > 0
    };
  }

  /**
   * Force unlock the scroll lock (useful for cleanup)
   */
  forceUnlock() {
    ScrollLock.lockCount = 0;
    ScrollLock.isLocked = false;
    const isiOS = /iP(ad|hone|od)/.test(navigator.userAgent || navigator.vendor || window.opera);
    
    if (isiOS) {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
      document.body.style.touchAction = "";
      document.body.style.overscrollBehavior = "";
      window.scrollTo(0, ScrollLock.scrollY || 0);
      
      // Remove touch event listeners
      this.removeTouchListeners();
    } else {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
      document.body.style.paddingLeft = ""; // Clear both for RTL support
      document.documentElement.style.removeProperty('--scrollbar-width');
      document.body.classList.remove('scroll-locked');
      ScrollLock.scrollbarWidth = 0;
    }
  }
} 

customElements.define("scroll-lock", ScrollLock);   